import React, { FormEvent, useCallback, useState } from "react";
import Button from "../../Buttons/Button";
import { Label } from "../../../style";
import { useApi } from "../../../hooks/api";
import { ModalHeader } from "../../ModalHeader";
import { useGlobal } from "../../../hooks/global";
import { ModalGeneric } from "../../ModalGeneric";
import { RangePicker } from "../../RangePicker";
import { ModalLoading } from "../../Loadings/ModalLoading";
import { FixRequiredSelect } from "../../Inputs/BaseSelects/FixRequiredSelect";
import { IOptionsProps } from "../../../interfaces";
import { checkInStatusOptions, reservationStatusOptions } from "../../../utils/options";
import { correct_date_filter, setUpConsultation } from "../../../services/generalServices";
import { downloadStyledXlsx } from "../../../services/exportExcel";
import { inactiveItens, primary, secondary } from "../../../theme";
import { Container, ContainerButtons, ContainerForm, Field, Form } from "./style";

interface IModalExportGuestsProps {
  onCancel: Function;
  openModal: boolean;
  backgroundVisible?: boolean;
}

const checkOutStatusOptions: IOptionsProps[] = [
  { label: "Não confirmado", value: "pending" },
  { label: "Confirmado", value: "done" },
];

const getRangeDates = (range: { from?: any; to?: any } | null) => {
  if (!range?.from && !range?.to) return { date_init: null, date_end: null };

  let from = range?.from ? correct_date_filter(range.from) : null;
  let to = range?.to ? correct_date_filter(range.to) : null;
  if (!from && to) from = to;
  if (!to && from) to = from;

  return { date_init: from, date_end: to };
};

const formatDateOnly = (value: any) => {
  if (!value) return "-";
  const raw = String(value);
  const datePart = raw.includes("T") ? raw.split("T")[0] : raw.split(" ")[0];
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "-";
  return `${day}/${month}/${year}`;
};

export const ModalExportGuests: React.FC<IModalExportGuestsProps> = (props) => {
  const { onCancel, openModal, backgroundVisible } = props;
  const { api } = useApi();
  const { theme, notify } = useGlobal();

  const [loading, setLoading] = useState(false);
  const [reservationStatus, setReservationStatus] = useState<IOptionsProps[]>([]);
  const [checkInStatus, setCheckInStatus] = useState<IOptionsProps[]>([]);
  const [checkOutStatus, setCheckOutStatus] = useState<IOptionsProps[]>([]);
  const [checkInRange, setCheckInRange] = useState<{ from: any; to: any }>({ from: null, to: null });
  const [checkOutRange, setCheckOutRange] = useState<{ from: any; to: any }>({ from: null, to: null });
  const [createdAtRange, setCreatedAtRange] = useState<{ from: any; to: any }>({ from: null, to: null });

  const handleExport = useCallback(async () => {
    setLoading(true);

    try {
      const checkInDates = getRangeDates(checkInRange);
      const checkOutDates = getRangeDates(checkOutRange);
      const createdAtDates = getRangeDates(createdAtRange);

      const filters_obj: any = {
        reservation_status: reservationStatus.map((item) => item.value).join(","),
        check_in_status: checkInStatus.map((item) => item.value).join(","),
        check_out_status: checkOutStatus.map((item) => item.value).join(","),
        check_in_date_init: checkInDates.date_init,
        check_in_date_end: checkInDates.date_end,
        check_out_date_init: checkOutDates.date_init,
        check_out_date_end: checkOutDates.date_end,
        created_at_date_init: createdAtDates.date_init,
        created_at_date_end: createdAtDates.date_end,
      };

      const query = setUpConsultation(filters_obj);
      const result = await api.get(`/stays-reservations/export-guests?${query}`);
      const guests = result.data.data || [];

      if (!guests.length) {
        notify("Nenhum hóspede encontrado para exportar!", "alert");
        setLoading(false);
        return;
      }

      const rows = guests.map((guest: any) => ({
        stays_id: guest.stays_id || "-",
        accommodation: guest.accommodation || "-",
        check_in_date: formatDateOnly(guest.check_in_date),
        guest_name: guest.guest_name || "-",
        guest_phone: guest.guest_phone || "-",
        guest_email: guest.guest_email || "-",
        reservation_status: guest.reservation_status || "-",
        check_in_status: guest.check_in_status || "-",
      }));

      const today = new Date();
      const fileDate = `${String(today.getDate()).padStart(2, "0")}-${String(today.getMonth() + 1).padStart(2, "0")}-${today.getFullYear()}`;

      downloadStyledXlsx({
        sheetName: "Hóspedes",
        fileName: `hospedes-${fileDate}.xlsx`,
        headers: [
          { key: "stays_id", label: "Stays ID" },
          { key: "accommodation", label: "Acomodação" },
          { key: "check_in_date", label: "Data Reserva" },
          { key: "guest_name", label: "Nome" },
          { key: "guest_phone", label: "Telefone" },
          { key: "guest_email", label: "E-mail" },
          { key: "reservation_status", label: "Status reserva" },
          { key: "check_in_status", label: "Status check-in" },
        ],
        rows,
      });
      notify("Excel gerado com sucesso!", "success");
      onCancel();
    } catch (err) {
      notify("Erro ao exportar os hóspedes!", "error");
    }

    setLoading(false);
  }, [api, reservationStatus, checkInStatus, checkOutStatus, checkInRange, checkOutRange, createdAtRange, notify, onCancel]);

  const handleSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleExport();
  }, [handleExport]);

  return (
    <ModalGeneric
      backgroundVisible={backgroundVisible}
      open={openModal}
      onClose={() => { }}
    >
      <Container theme={theme}>
        <ModalHeader
          title="Exportar hóspedes"
          subTitle="Aplique os filtros para gerar o Excel. Nos status você pode selecionar mais de uma opção."
          onCancel={() => onCancel()}
          theme={theme}
        />
        <Form onSubmit={handleSubmit}>
          <ContainerForm>
            <Field>
              <Label>Status da reserva</Label>
              <FixRequiredSelect
                placeholder=""
                isClearable
                isMulti
                closeMenuOnSelect={false}
                options={reservationStatusOptions}
                value={reservationStatus}
                onChange={(value: any) => setReservationStatus(value || [])}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </Field>
            <Field>
              <Label>Status do check-in</Label>
              <FixRequiredSelect
                placeholder=""
                isClearable
                isMulti
                closeMenuOnSelect={false}
                options={checkInStatusOptions}
                value={checkInStatus}
                onChange={(value: any) => setCheckInStatus(value || [])}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </Field>
            <Field>
              <Label>Status do check-out</Label>
              <FixRequiredSelect
                placeholder=""
                isClearable
                isMulti
                closeMenuOnSelect={false}
                options={checkOutStatusOptions}
                value={checkOutStatus}
                onChange={(value: any) => setCheckOutStatus(value || [])}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </Field>
            <Field>
              <Label>Período (entrada)</Label>
              <div style={{ height: 38 }}>
                <RangePicker onChange={setCheckInRange} />
              </div>
            </Field>
            <Field>
              <Label>Período (saída)</Label>
              <div style={{ height: 38 }}>
                <RangePicker onChange={setCheckOutRange} />
              </div>
            </Field>
            <Field>
              <Label>Período (criação)</Label>
              <div style={{ height: 38 }}>
                <RangePicker onChange={setCreatedAtRange} />
              </div>
            </Field>
          </ContainerForm>
          <ContainerButtons theme={theme}>
            <Button
              onClick={() => onCancel()}
              background={primary[theme]}
              color={inactiveItens[theme]}
              style={{ height: 40, margin: 0, padding: "0 16px", boxShadow: "0.125rem 0.125rem 0.5rem #57545419", justifyContent: "center", alignItems: "center", fontSize: 16 }}
              type="button"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              background={secondary[theme]}
              color="#FFF"
              disabled={loading}
              style={{ height: 40, margin: 0, padding: "0 16px", boxShadow: "0.125rem 0.125rem 0.5rem #57545419", justifyContent: "center", alignItems: "center", fontSize: 16 }}
            >
              Exportar
            </Button>
          </ContainerButtons>
        </Form>
        {loading && <ModalLoading loading={loading} theme={theme} />}
      </Container>
    </ModalGeneric>
  );
};
